(function () {
  'use strict';

  var SEARCH_LIMIT = 5;
  var DEBOUNCE_MS = 200;

  function escapeHtml(text) {
    var div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  function escapeRegExp(text) {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function highlightTerm(text, term) {
    if (!term || !text) {
      return escapeHtml(text || '');
    }

    var regex = new RegExp('(' + escapeRegExp(term) + ')', 'gi');
    var parts = text.split(regex);

    return parts
      .map(function (part) {
        if (part.toLowerCase() === term.toLowerCase()) {
          return '<span class="redmond-search-highlight">' + escapeHtml(part) + '</span>';
        }
        return escapeHtml(part);
      })
      .join('');
  }

  function stripHtml(html) {
    if (!html) {
      return '';
    }
    var div = document.createElement('div');
    div.innerHTML = html;
    return (div.textContent || div.innerText || '').trim();
  }

  function truncate(text, max) {
    if (!text || text.length <= max) {
      return text || '';
    }
    return text.slice(0, max).trim() + '…';
  }

  function getStoreBaseUrl(identity) {
    var normalizedIdentity = (identity || '').toString().toLowerCase();
    var config = window.CART_SYNC_CONFIG || {};
    var storeUrls = config.STORE_URLS || {};

    if (
      normalizedIdentity.indexOf('equine') !== -1 ||
      normalizedIdentity.indexOf('horse') !== -1 ||
      normalizedIdentity.indexOf('daily gold') !== -1
    ) {
      return storeUrls.equine || 'https://equine.redmond.com';
    }

    if (normalizedIdentity.indexOf('agriculture') !== -1 || normalizedIdentity.indexOf(' ag ') !== -1) {
      return storeUrls.agriculture || 'https://agriculture.redmond.com';
    }

    if (normalizedIdentity.indexOf('hunt') !== -1 || normalizedIdentity.indexOf('trophy') !== -1) {
      return storeUrls.hunt || 'https://hunt.redmond.com';
    }

    return storeUrls.living || 'https://living.redmond.com';
  }

  function buildCanonicalProductUrl(item) {
    if (!item) {
      return '';
    }

    if (window.RedmondCartSync && typeof window.RedmondCartSync.getCanonicalProductUrl === 'function') {
      return window.RedmondCartSync.getCanonicalProductUrl(item) || item.url || '';
    }

    var identity = [
      item.vendor,
      item.product_type,
      item.product_title,
      item.title,
      item.handle
    ]
      .filter(Boolean)
      .join(' ');
    var baseUrl = getStoreBaseUrl(identity);
    var path = item.url || ('/products/' + (item.handle || ''));

    try {
      path = new URL(path, window.location.origin).pathname;
    } catch (error) {
      path = String(path).split('?')[0].split('#')[0];
    }

    try {
      return new URL(path, baseUrl).toString();
    } catch (error) {
      return String(baseUrl).replace(/\/$/, '') + path;
    }
  }

  function getCanonicalProductUrl(item) {
    if (window.RedmondCartSync && typeof window.RedmondCartSync.getCanonicalProductUrl === 'function') {
      return window.RedmondCartSync.getCanonicalProductUrl(item) || item.url;
    }
    return buildCanonicalProductUrl(item);
  }

  var INDEX_CACHE_KEY = 'redmond_search_index_v1';
  var INDEX_TTL_MS = 30 * 60 * 1000;
  var INDEX_MAX_PAGES = 8;
  var productIndexPromise = null;

  function normalize(text) {
    return (text || '')
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[^a-z0-9]+/g, '');
  }

  function tokenize(text) {
    return (text || '')
      .toLowerCase()
      .normalize('NFKD')
      .split(/[^a-z0-9]+/)
      .filter(Boolean);
  }

  function levenshtein(a, b, max) {
    if (a === b) {
      return 0;
    }
    if (Math.abs(a.length - b.length) > max) {
      return max + 1;
    }

    var prev = [];
    var i;
    for (i = 0; i <= b.length; i++) {
      prev[i] = i;
    }

    for (i = 1; i <= a.length; i++) {
      var curr = [i];
      var rowMin = i;
      for (var j = 1; j <= b.length; j++) {
        var cost = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
        curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
        if (curr[j] < rowMin) {
          rowMin = curr[j];
        }
      }
      if (rowMin > max) {
        return max + 1;
      }
      prev = curr;
    }

    return prev[b.length];
  }

  function buildIndexItem(product) {
    var image =
      (product.images && product.images[0] && product.images[0].src) ||
      (product.featured_image && product.featured_image.src) ||
      '';
    var tags = Array.isArray(product.tags) ? product.tags.join(' ') : product.tags || '';

    return {
      title: product.title,
      url: buildCanonicalProductUrl(product),
      image: image,
      type: product.product_type || '',
      titleNorm: normalize(product.title),
      words: tokenize(product.title + ' ' + (product.product_type || '') + ' ' + tags),
    };
  }

  function readIndexCache() {
    try {
      var raw = window.sessionStorage.getItem(INDEX_CACHE_KEY);
      if (!raw) {
        return null;
      }
      var parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.items) || Date.now() - parsed.ts > INDEX_TTL_MS) {
        return null;
      }
      return parsed.items;
    } catch (error) {
      return null;
    }
  }

  function writeIndexCache(items) {
    try {
      window.sessionStorage.setItem(INDEX_CACHE_KEY, JSON.stringify({ ts: Date.now(), items: items }));
    } catch (error) {
      /* ignore quota / privacy-mode errors */
    }
  }

  function fetchAllProducts() {
    var items = [];

    function fetchPage(page) {
      return fetch('/products.json?limit=250&page=' + page)
        .then(function (response) {
          if (!response.ok) {
            throw new Error('products.json request failed');
          }
          return response.json();
        })
        .then(function (data) {
          var products = (data && data.products) || [];
          products.forEach(function (product) {
            items.push(buildIndexItem(product));
          });
          if (products.length === 250 && page < INDEX_MAX_PAGES) {
            return fetchPage(page + 1);
          }
          return items;
        });
    }

    return fetchPage(1);
  }

  function ensureProductIndex() {
    if (productIndexPromise) {
      return productIndexPromise;
    }

    var cached = readIndexCache();
    if (cached) {
      productIndexPromise = Promise.resolve(cached);
      return productIndexPromise;
    }

    productIndexPromise = fetchAllProducts()
      .then(function (items) {
        writeIndexCache(items);
        return items;
      })
      .catch(function () {
        productIndexPromise = null;
        return [];
      });

    return productIndexPromise;
  }

  function fuzzyMatch(term, items) {
    var tn = normalize(term);
    if (tn.length < 2 || !items || !items.length) {
      return [];
    }

    var maxDist = tn.length <= 4 ? 1 : tn.length <= 7 ? 2 : 3;

    var scored = [];
    items.forEach(function (item) {
      var score = 0;

      var idx = item.titleNorm.indexOf(tn);
      if (idx !== -1) {
        score = 100 - Math.min(idx, 40);
      } else {
        for (var w = 0; w < item.words.length; w++) {
          var word = item.words[w];
          if (word.indexOf(tn) === 0) {
            score = Math.max(score, 80);
          } else {
            var dist = levenshtein(tn, word, maxDist);
            if (dist <= maxDist) {
              score = Math.max(score, 60 - dist * 10);
            }
          }
        }
      }

      if (score > 0) {
        scored.push({ score: score, item: item });
      }
    });

    scored.sort(function (a, b) {
      return b.score - a.score;
    });

    return scored.slice(0, SEARCH_LIMIT).map(function (entry) {
      return {
        url: entry.item.url,
        title: entry.item.title,
        featured_image: { url: entry.item.image },
        body: '',
      };
    });
  }

  function RedmondSearch(root, options) {
    options = options || {};
    this.root = root;
    this.context = root.getAttribute('data-redmond-search-context') || 'desktop';
    this.form = root.querySelector('.redmond-search-form');
    this.input = root.querySelector('.redmond-search-input');
    this.resultsEl = root.querySelector('[data-redmond-search-results]');
    this.closeBtn = root.querySelector('.redmond-search-close');
    this.submitBtn = root.querySelector('.redmond-search-submit');
    this.headerBar = options.headerBar || null;
    this.overlay = options.overlay || null;
    this.toggleBtns = options.toggleBtns || [];
    this.debounceTimer = null;
    this.abortController = null;
    this.activeIndex = -1;
    this.flatItems = [];
    this.currentTerm = '';
  }

  RedmondSearch.prototype.init = function () {
    if (!this.input || !this.resultsEl) {
      return;
    }

    this.bindEvents();
  };

  RedmondSearch.prototype.isOpen = function () {
    return this.headerBar && this.headerBar.classList.contains('is-search-open');
  };

  RedmondSearch.prototype.bindEvents = function () {
    var self = this;

    this.input.addEventListener('input', function (event) {
      self.activeIndex = -1;
      self.debouncedFetch(event.target.value);
    });

    this.input.addEventListener('focus', function () {
      self.fetchResults(self.input.value);
    });

    this.input.addEventListener('keydown', function (event) {
      self.handleKeyDown(event);
    });

    if (this.form) {
      this.form.addEventListener('submit', function (event) {
        event.preventDefault();
        self.goToSearch();
      });
    }

    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        self.close();
      });
    }

    if (this.submitBtn) {
      this.submitBtn.addEventListener('click', function (event) {
        event.preventDefault();
        self.goToSearch();
      });
    }

    if (this.context === 'desktop' && this.headerBar) {
      this.toggleBtns.forEach(function (btn) {
        btn.addEventListener('click', function (event) {
          event.preventDefault();
          event.stopPropagation();
          self.toggle();
        });
      });

      document.addEventListener('mousedown', function (event) {
        if (!self.isOpen()) {
          return;
        }

        if (event.target.closest('[data-search-toggle="true"]')) {
          return;
        }

        if (event.target.closest('.redmond-search-panel')) {
          return;
        }

        self.close();
      });

      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && self.isOpen()) {
          self.close();
        }
      });
    }
  };

  RedmondSearch.prototype.toggle = function () {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  };

  RedmondSearch.prototype.open = function () {
    if (!this.headerBar || !this.overlay) {
      return;
    }

    document.querySelectorAll('.dropdown.open').forEach(function (dropdown) {
      dropdown.classList.remove('open');
    });

    this.headerBar.classList.add('is-search-open');
    this.overlay.hidden = false;
    this.overlay.setAttribute('aria-hidden', 'false');

    this.toggleBtns.forEach(function (btn) {
      btn.setAttribute('aria-expanded', 'true');
    });

    if (this.input) {
      this.input.setAttribute('aria-expanded', 'true');
    }

    var self = this;
    window.setTimeout(function () {
      if (self.input) {
        self.input.focus();
      }
    }, 50);
  };

  RedmondSearch.prototype.close = function () {
    if (!this.headerBar || !this.overlay) {
      if (this.input) {
        this.input.blur();
      }
      this.reset();
      return;
    }

    this.headerBar.classList.remove('is-search-open');
    this.overlay.hidden = true;
    this.overlay.setAttribute('aria-hidden', 'true');

    this.toggleBtns.forEach(function (btn) {
      btn.setAttribute('aria-expanded', 'false');
    });

    this.reset();
  };

  RedmondSearch.prototype.reset = function () {
    if (this.input) {
      this.input.value = '';
      this.input.setAttribute('aria-expanded', 'false');
      this.input.blur();
    }

    this.currentTerm = '';
    this.activeIndex = -1;
    this.flatItems = [];

    if (this.resultsEl) {
      this.resultsEl.innerHTML = '';
    }

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  };

  RedmondSearch.prototype.debouncedFetch = function (query) {
    var self = this;

    window.clearTimeout(this.debounceTimer);
    this.debounceTimer = window.setTimeout(function () {
      self.fetchResults(query);
    }, DEBOUNCE_MS);
  };

  RedmondSearch.prototype.fetchResults = function (query) {
    var self = this;
    var term = (query || '').trim();

    this.currentTerm = term;

    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();

    if (term) {
      this.renderLoading();
    } else {
      this.resultsEl.innerHTML = '';
      return;
    }

    var url =
      '/search/suggest.json?q=' +
      encodeURIComponent(term) +
      '&resources[type]=product,collection,query' +
      '&resources[limit]=' +
      SEARCH_LIMIT +
      '&resources[limit_scope]=each';

    fetch(url, { signal: this.abortController.signal })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Search request failed');
        }
        return response.json();
      })
      .then(function (data) {
        if (self.currentTerm !== term) {
          return;
        }

        var results = (data.resources && data.resources.results) || {};
        var products = results.products || [];

        if (products.length === 0 && term) {
          ensureProductIndex().then(function (items) {
            if (self.currentTerm !== term) {
              return;
            }
            var fuzzy = fuzzyMatch(term, items);
            if (fuzzy.length) {
              data.resources = data.resources || {};
              data.resources.results = data.resources.results || {};
              data.resources.results.products = fuzzy;
            }
            self.renderResults(data, term);
          });
          return;
        }

        self.renderResults(data, term);
      })
      .catch(function (error) {
        if (error.name === 'AbortError') {
          return;
        }
        self.renderEmpty(term);
      });
  };

  RedmondSearch.prototype.renderLoading = function () {
    this.resultsEl.innerHTML = '<div class="redmond-search-loading">Loading...</div>';
  };

  RedmondSearch.prototype.renderEmpty = function (term) {
    this.flatItems = [];
    this.resultsEl.innerHTML =
      '<div class="redmond-search-empty">No results found for <q>' +
      escapeHtml(term) +
      '</q></div>';
  };

  RedmondSearch.prototype.renderResults = function (data, term) {
    var results = (data.resources && data.resources.results) || {};
    var products = results.products || [];
    var collections = results.collections || [];
    var queries = results.queries || [];

    this.flatItems = [];
    this.activeIndex = -1;

    var total = products.length + collections.length + queries.length;

    if (!total && !term) {
      this.resultsEl.innerHTML = '';
      return;
    }

    if (!total && term) {
      this.renderEmpty(term);
      return;
    }

    var html = '';
    var offset = 0;

    if (products.length > 0) {
      html += this.buildProductsSection(products, term, offset);
      offset += products.length;
    }

    if (collections.length > 0) {
      html += this.buildCollectionsSection(collections, term, offset);
      offset += collections.length;
    }

    if (queries.length > 0) {
      html += this.buildPopularSection(queries, offset);
    }

    this.resultsEl.innerHTML = html;
    this.bindResultInteractions();
  };

  RedmondSearch.prototype.buildProductsSection = function (products, term, offset) {
    var self = this;
    var cards = products
      .map(function (product, index) {
        var itemIndex = offset + index;
        var productUrl = getCanonicalProductUrl(product);
        self.flatItems.push({ type: 'link', url: productUrl });

        var imageUrl =
          (product.featured_image && product.featured_image.url) ||
          (product.image && product.image) ||
          '';
        var description = truncate(stripHtml(product.body), 80) || '';

        return (
          '<div class="redmond-search-product-card" data-search-index="' +
          itemIndex +
          '">' +
          '<a href="' +
          escapeHtml(productUrl) +
          '" class="redmond-search-product-link">' +
          (imageUrl
            ? '<img class="redmond-search-product-image" src="' +
              escapeHtml(imageUrl) +
              '" alt="' +
              escapeHtml(product.title) +
              '" loading="lazy">'
            : '<div class="redmond-search-product-image"></div>') +
          '<div class="redmond-search-product-content">' +
          '<p class="redmond-search-product-title">' +
          highlightTerm(product.title, term) +
          '</p>' +
          (description
            ? '<p class="redmond-search-product-description">' + escapeHtml(description) + '</p>'
            : '') +
          '</div>' +
          '</a>' +
          '</div>'
        );
      })
      .join('');

    return (
      '<div class="redmond-search-section">' +
      '<h3 class="redmond-search-section-title">Products</h3>' +
      '<div class="redmond-search-product-list">' +
      cards +
      '</div>' +
      '</div>'
    );
  };

  RedmondSearch.prototype.buildCollectionsSection = function (collections, term, offset) {
    var self = this;
    var items = collections
      .map(function (collection, index) {
        var itemIndex = offset + index;
        self.flatItems.push({ type: 'link', url: collection.url });

        return (
          '<div class="redmond-search-collection-item" data-search-index="' +
          itemIndex +
          '">' +
          '<a href="' +
          escapeHtml(collection.url) +
          '" class="redmond-search-product-link">' +
          '<div class="redmond-search-product-content">' +
          '<p class="redmond-search-collection-title">' +
          highlightTerm(collection.title, term) +
          '</p>' +
          '</div>' +
          '</a>' +
          '</div>'
        );
      })
      .join('');

    return (
      '<div class="redmond-search-section">' +
      '<h3 class="redmond-search-section-title">Related Collections</h3>' +
      items +
      '</div>'
    );
  };

  RedmondSearch.prototype.buildPopularSection = function (queries, offset) {
    var self = this;
    var pills = queries
      .filter(Boolean)
      .map(function (query, index) {
        var itemIndex = offset + index;
        var text = query.text || query.styled_text || '';
        self.flatItems.push({ type: 'query', text: text });

        return (
          '<button type="button" class="redmond-search-popular-pill" data-search-index="' +
          itemIndex +
          '" data-query="' +
          escapeHtml(text) +
          '">' +
          '<p class="redmond-search-popular-text">' +
          escapeHtml(truncate(text, 40)) +
          '</p>' +
          '</button>'
        );
      })
      .join('');

    return (
      '<div class="redmond-search-popular-section">' +
      '<h3 class="redmond-search-section-title">Popular searches</h3>' +
      '<div class="redmond-search-popular-grid">' +
      pills +
      '</div>' +
      '</div>'
    );
  };

  RedmondSearch.prototype.bindResultInteractions = function () {
    var self = this;

    this.resultsEl.querySelectorAll('[data-query]').forEach(function (pill) {
      pill.addEventListener('click', function (event) {
        event.preventDefault();
        var query = pill.getAttribute('data-query') || '';
        self.input.value = query;
        self.activeIndex = -1;
        self.goToSearch();
      });
    });

    this.resultsEl.querySelectorAll('a[href]').forEach(function (link) {
      link.addEventListener('click', function () {
        if (self.context === 'cart-empty' && typeof window.closeCartDrawer === 'function') {
          window.closeCartDrawer();
        }
        self.close();
      });
    });

    this.updateActiveState();
  };

  RedmondSearch.prototype.updateActiveState = function () {
    var productCards = this.resultsEl.querySelectorAll('.redmond-search-product-card');
    var collectionItems = this.resultsEl.querySelectorAll('.redmond-search-collection-item');
    var pills = this.resultsEl.querySelectorAll('.redmond-search-popular-pill');

    productCards.forEach(function (card) {
      var index = parseInt(card.getAttribute('data-search-index'), 10);
      var isActive = index === this.activeIndex;
      card.classList.toggle('redmond-search-product-card--active', isActive);
      if (isActive) {
        card.setAttribute('data-active', 'true');
      } else {
        card.removeAttribute('data-active');
      }
    }.bind(this));

    collectionItems.forEach(function (item) {
      var index = parseInt(item.getAttribute('data-search-index'), 10);
      var isActive = index === this.activeIndex;
      item.classList.toggle('redmond-search-collection-item--active', isActive);
      if (isActive) {
        item.setAttribute('data-active', 'true');
      } else {
        item.removeAttribute('data-active');
      }
    }.bind(this));

    pills.forEach(function (pill) {
      var index = parseInt(pill.getAttribute('data-search-index'), 10);
      var isActive = index === this.activeIndex;
      pill.classList.toggle('redmond-search-popular-pill--active', isActive);
      if (isActive) {
        pill.setAttribute('data-active', 'true');
      } else {
        pill.removeAttribute('data-active');
      }
    }.bind(this));

    var activeEl = this.resultsEl.querySelector('[data-active="true"]');
    if (activeEl) {
      activeEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  };

  RedmondSearch.prototype.handleKeyDown = function (event) {
    var total = this.flatItems.length;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (total === 0) {
        return;
      }
      this.activeIndex = (this.activeIndex + 1) % total;
      this.updateActiveState();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (total === 0) {
        return;
      }
      this.activeIndex = this.activeIndex <= 0 ? total - 1 : this.activeIndex - 1;
      this.updateActiveState();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      var item = this.flatItems[this.activeIndex];

      if (item) {
        if (item.type === 'query') {
          this.input.value = item.text;
          this.activeIndex = -1;
          this.fetchResults(item.text);
        } else if (item.url) {
          window.location.href = item.url;
        }
      } else {
        this.goToSearch();
      }
    }
  };

  RedmondSearch.prototype.goToSearch = function () {
    var term = (this.input.value || '').trim();
    var url = '/search' + (term ? '?q=' + encodeURIComponent(term) : '');
    if (this.context === 'cart-empty' && typeof window.closeCartDrawer === 'function') {
      window.closeCartDrawer();
    }
    window.location.href = url;
  };

  window.initRedmondSearch = function (root, options) {
    if (!root) {
      return null;
    }

    if (root._redmondSearchInstance) {
      return root._redmondSearchInstance;
    }

    var instance = new RedmondSearch(root, options || {});
    instance.init();
    root._redmondSearchInstance = instance;
    return instance;
  };

  function bootRedmondSearch() {
    var headerBar = document.querySelector('.redmond-header-bar');
    var overlay = document.querySelector('[data-redmond-search-overlay]');
    var toggleBtns = document.querySelectorAll('[data-search-toggle="true"]');
    var desktopRoot = overlay ? overlay.querySelector('[data-redmond-search]') : null;
    var isMobileViewport = window.matchMedia('(max-width: 960px)').matches;

    if (desktopRoot && !isMobileViewport) {
      window.initRedmondSearch(desktopRoot, {
        headerBar: headerBar,
        overlay: overlay,
        toggleBtns: toggleBtns,
      });
    } else if (headerBar && overlay) {
      headerBar.classList.remove('is-search-open');
      overlay.hidden = true;
      overlay.setAttribute('aria-hidden', 'true');
    }

    document.querySelectorAll('[data-redmond-search]').forEach(function (root) {
      if (root === desktopRoot) {
        return;
      }

      window.initRedmondSearch(root, {});
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootRedmondSearch);
  } else {
    bootRedmondSearch();
  }
})();
